import type { TripRoutePoint } from '@/services/trips/tripsService';
import type { TrackPoint } from '@/types/trackPlayback';

export interface JammingPoint {
  jamming?: boolean;
}

export interface ColoredRouteSegment {
  positions: [number, number][];
  jamming: boolean;
}

function edgeHasJamming<T extends JammingPoint>(a: T, b: T): boolean {
  return a.jamming === true || b.jamming === true;
}

export function splitPointsByJamming<T extends JammingPoint>(
  segment: T[],
  toLatLng: (point: T) => [number, number]
): ColoredRouteSegment[] {
  if (segment.length < 2) {
    return [];
  }

  const output: ColoredRouteSegment[] = [];
  let startIndex = 0;

  while (startIndex < segment.length - 1) {
    const jamming = edgeHasJamming(segment[startIndex], segment[startIndex + 1]);
    let endIndex = startIndex + 1;

    while (
      endIndex < segment.length - 1 &&
      edgeHasJamming(segment[endIndex], segment[endIndex + 1]) === jamming
    ) {
      endIndex++;
    }

    output.push({
      positions: segment.slice(startIndex, endIndex + 1).map(toLatLng),
      jamming,
    });
    startIndex = endIndex;
  }

  return output;
}

export function splitRouteSegmentByJamming(segment: TripRoutePoint[]): ColoredRouteSegment[] {
  return splitPointsByJamming(segment, (point) => [point.coordinates[1], point.coordinates[0]]);
}

export function splitTrackPointsByJamming(points: TrackPoint[]): ColoredRouteSegment[] {
  return splitPointsByJamming(points, (point) => [point.lat, point.lng]);
}

export function countJammingPoints<T extends JammingPoint>(segment: T[]): number {
  return segment.filter((p) => p.jamming === true).length;
}
